// @ts-ignore

import React, { useState, useEffect } from "react";
import { Play, Link, AlertCircle, CheckCircle } from "lucide-react";
import ApiService from "../services/api/api";
import { GoogleAdsAuthManager } from "../utils/auth";

const GoogleAdsAuth = ({ onAuthSuccess }: any) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    checkAuthStatus();
    handleOAuthCallback();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const status = await ApiService.getAuthStatus();
      setIsConnected(status.isConnected);

      // Update auth manager with the status
      GoogleAdsAuthManager.updateConnectionStatus(status.isConnected);

      if (status.isConnected && onAuthSuccess) {
        onAuthSuccess();
      }
    } catch (error) {
      console.error("Failed to check auth status:", error);
      // If auth check fails, clear stored auth data
      GoogleAdsAuthManager.clearAuthData();
      setIsConnected(false);
    }
  };

  const handleOAuthCallback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log(urlParams, "urlParams");
    const code = urlParams.get("code");
    const state = urlParams.get("state") || null;
    const urlError = urlParams.get("error") || null;

    if (code) {
      try {
        setLoading(true);
        const result = await ApiService.handleAuthCallback(code, state);
        console.log("Result:", result);
        if (result.success) {
          setIsConnected(true);
          setError(null);

          // Save authentication data to localStorage
          GoogleAdsAuthManager.saveAuthData({
            userId: result.userId || "default",
            accessToken: result.sessionToken,
            refreshToken: result.sessionToken,
            expiresAt: result.expiresAt
              ? new Date(result.expiresAt).getTime()
              : undefined,
            isConnected: true,
          });

          if (onAuthSuccess) {
            onAuthSuccess();
          }
          // Clean up URL
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      } catch (err: any) {
        setError(err.message);
        GoogleAdsAuthManager.clearAuthData();
      } finally {
        setLoading(false);
      }
    }
  };

  const startAuth = async () => {
    try {
      setLoading(true);
      setError(null);
      const { authUrl } = await ApiService.startGoogleAuth();
      window.location.href = authUrl;
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const disconnect = async () => {
    try {
      setLoading(true);
      await ApiService.disconnectGoogleAds();
      setIsConnected(false);
      setError(null);

      // Clear authentication data from localStorage
      GoogleAdsAuthManager.clearAuthData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Processing...</span>
      </div>
    );
  }

  if (isConnected) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Google Ads Connected
              </h3>
              <p className="text-sm text-green-600">
                Your Google Ads account is successfully connected.
              </p>
            </div>
          </div>
          <button
            onClick={disconnect}
            disabled={loading}
            className="bg-red-100 hover:bg-red-200 text-red-800 text-sm px-3 py-1 rounded transition-colors disabled:opacity-50"
          >
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center py-12">
      <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
        <Play className="w-8 h-8 text-blue-600" />
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Connect Your Google Ads Account
      </h2>

      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        Connect your Google Ads account to view campaign performance, manage
        keywords, and analyze your advertising data.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Before connecting:</p>
            <ul className="text-left space-y-1">
              <li>• Ensure you have Google Ads account access</li>
              <li>• You'll be redirected to Google for authorization</li>
              <li>• Grant permissions to view your ads data</li>
            </ul>
          </div>
        </div>
      </div>

      <button
        onClick={startAuth}
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center mx-auto disabled:opacity-50"
      >
        <Link className="w-5 h-5 mr-2" />
        Connect Google Ads
      </button>
    </div>
  );
};

export default GoogleAdsAuth;
